<script>
import CockpitMatryoshkaInheritanceTreeSectionItem
from '@/components/cockpit/matryoshka/inheritance-tree/CockpitMatryoshkaInheritanceTreeSectionItem.vue';
import { mapGetters, mapActions } from 'vuex';
import LabelArchiveContentsBadge from '../../LabelArchiveContentsBadge.vue';

export default {
  name: 'CockpitMatryoshkaInheritanceTreeSection',
  props: {
    upperLevel: Boolean,
    lowerLevel: Boolean,
    rootLabelArchiveId: Number,
    showConnectButton: Boolean,
    label: String,
    selectedIds: Array,
    items: Array,
    labelArchive: Object,
  },
  data() {
    return {
      parentElementDebounce: null,
      parentElement: null,
    };
  },
  emits: [
    'connect',
    'disconnect',
    'disconnect-selected',
    'select-item-id',
    'go-to-serial-number',
    'reverse-select',
  ],
  components: {
    CockpitMatryoshkaInheritanceTreeSectionItem,
    LabelArchiveContentsBadge,
  },
  computed: {
    ...mapGetters('packingSettings', [
      'getUnitToUnitRelation',
      'getLowerLevelUnit',
      'getHigherLevelUnit',
    ]),
    ...mapGetters('emptyLabelArchive', [
      'isLabelArchiveEmpty',
      'emptyAmount',
    ]),
    ...mapGetters([
      'globalSettings',
    ]),
    lowerUnit() {
      return this.getLowerLevelUnit(this.labelArchive.unitId, this.labelArchive.skuId);
    },
    higherUnit() {
      return this.getHigherLevelUnit(this.labelArchive.unitId, this.labelArchive.skuId);
    },
    higherUnitParentRelations() {
      if (!this.higherUnit) return [];
      return this.labelArchive.containers.filter(
        container => container.containerUnitId === this.higherUnit.id,
      );
    },
    lowerChildren() {
      if (!this.lowerUnit) return [];
      return this.labelArchive.containedLabels.filter(
        label => label.unitId === this.lowerUnit.id,
      );
    },
    emptyLowerChildrenAmount() {
      if (!this.lowerUnit) return 0;
      return this.emptyAmount(this.lowerChildren.map(l => l.id));
    },
    lowerUnitChildrenAmount() {
      if (!this.lowerUnit) return 0;
      return this.lowerChildren.length - this.emptyLowerChildrenAmount;
    },
    maxAmount() {
      if (!this.lowerUnit) return 0;
      return this.getUnitToUnitRelation(
        this.lowerUnit.id,
        this.labelArchive.unitId,
        this.labelArchive.skuId,
      )?.amount || 0;
    },
    isFull() {
      if (!this.lowerUnit) return false;
      return this.lowerUnitChildrenAmount >= this.maxAmount;
    },
    canUnlinkForThisElement() {
      if (this.isFirstUnit) return true;
      if (this.isMiddleUnit) {
        if (this.upperLevel) {
          if (!this.parentElement && this.higherUnitParentRelations.length > 0) return false;
          if (this.parentElement.labelStatus === 'Active') return false;
          return true;
        }
        return true;
      }
      if (this.isLastUnit) return this.labelArchive.labelStatus !== 'Active';
      return false;
    },
    canUnlinkSelectedIds() {
      return this.selectedIds.every(id => {
        const item = this.items.find(i => i.id === id);
        return item && item.containedLabels.length === 0;
      });
    },
    sortedItems() {
      return [...this.items].sort((a, b) => {
        if (this.isLabelArchiveEmpty(a.id) && !this.isLabelArchiveEmpty(b.id)) return -1;
        if (!this.isLabelArchiveEmpty(a.id) && this.isLabelArchiveEmpty(b.id)) return 1;
        if (a.scanIndex === null && b.scanIndex !== null) return -1;
        if (a.scanIndex !== null && b.scanIndex === null) return 1;
        if (a.scanIndex && b.scanIndex) return b.scanIndex - a.scanIndex;
        return 0;
      });
    },
    isMiddleUnit() {
      return Boolean(this.lowerUnit && this.higherUnit);
    },
    isLastUnit() {
      return Boolean(this.lowerUnit && !this.higherUnit);
    },
    isFirstUnit() {
      return Boolean(!this.lowerUnit && this.higherUnit);
    },
  },
  watch: {
    higherUnitParentRelations: {
      handler() {
        this.getParentElement();
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    ...mapActions('labelArchive', [
      'getLabelArchiveByIds',
    ]),
    isIdSelected(id) {
      return (this.selectedIds || []).includes(id);
    },
    canRemoveFromParent() {
      return this.labelArchive.containedLabels.length < 1;
    },
    reverseSelect() {
      this.$emit('reverse-select', this.selectedIds);
    },
    async getParentElement() {
      if (this.parentElementDebounce) {
        clearTimeout(this.parentElementDebounce);
      }
      this.parentElementDebounce = setTimeout(async () => {
        if (!this.higherUnitParentRelations.length) return;
        const { data } = await this.getLabelArchiveByIds({
          params: {
            plantCode: this.globalSettings.plantCode,
            query: {
              ids: [this.higherUnitParentRelations[0].containerId],
            },
          },
        });
        this.parentElement = data[0];
      }, 100);
    },
  },
};
</script>

<template>
  <div
    class="inheritance-tree"
    :class="{
      'flex-grow-1 mb-0 d-flex flex-column': lowerLevel,
    }"
  >
    <div class="label">
      <div class="mb-3 d-flex justify-content-between">
        <div class="mb-n1 mr-2">
          {{ label }}
        </div>

        <LabelArchiveContentsBadge
          v-if="lowerLevel"
          :label-archive="labelArchive"
        />
      </div>

      <div class="mx-auto" />
    </div>

    <div
      style="margin-left: 30px;"
      class="d-flex flex-column"
    >
      <div
        v-if="selectedIds.length > 0"
        class="text-muted mb-1 small"
      >
        Zaznaczono <strong class="h5 mb-0">{{ selectedIds.length }}</strong> / {{ items.length }} elementów
      </div>

      <div class="w-100 d-flex justify-content-between mb-4 ">
        <BButton
          v-if="upperLevel
            && !isLastUnit
            && showConnectButton
            && higherUnitParentRelations.length === 0"
          variant="primary"
          class="connect-button ml-auto"
          @click="$emit('connect')"
        >
          <i class="fas fa-link" />

          {{ $t('cockpit.connectElement') }}
        </BButton>

        <BButton
          v-if="lowerLevel
            && showConnectButton
            && selectedIds
            && !isFull
            && selectedIds.length === 0"
          variant="primary"
          class="connect-button ml-auto"
          @click="$emit('connect')"
        >
          <i class="fas fa-link" />

          {{ $t('cockpit.connectElement') }}
        </BButton>

        <BButton
          v-if="selectedIds.length > 0 && !isLastUnit"
          variant="secondary"
          class="reverse-select-button"
          @click="reverseSelect()"
        >
          <i class="fas fa-arrows-alt-h" />

          {{ $t('cockpit.reverseSelect') }}
        </BButton>
        <BButton
          v-if="selectedIds.length > 0 && !isLastUnit"
          variant="secondary"
          class="connect-button"
          :disabled="!canUnlinkSelectedIds"
          :class="{
            'cursor-not-allowed': !canUnlinkSelectedIds,
          }"
          :title="canUnlinkSelectedIds ? undefined : 'Nie można odłączyć elementu, który ma elementy podrzędne'"
          @click="canUnlinkSelectedIds ? $emit('disconnect-selected') : undefined"
        >
          <i class="fas fa-unlink" />

          {{ $t('cockpit.disconnectSelectedElements') }} ({{ selectedIds.length }})
        </BButton>
      </div>
    </div>

    <div class="children">
      <CockpitMatryoshkaInheritanceTreeSectionItem
        v-for="item in sortedItems"
        :key="item.id"
        :item="item"
        :root-label-archive-id="rootLabelArchiveId"
        :level-icon="upperLevel ? 'fas fa-arrow-up-short-wide' : 'fas fa-arrow-down-short-wide'"
        :no-operations="!canUnlinkForThisElement"
        :can-select="lowerLevel"
        :can-remove-from-parent="canRemoveFromParent(item)"
        :is-selected="isIdSelected(item.id)"
        @go-to-serial-number="$emit('go-to-serial-number', $event)"
        @select-item-id="$emit('select-item-id', $event)"
        @disconnect="$emit('disconnect', item.id)"
        @utilize-empty="$emit('utilize-empty', item.id)"
      />

      <div
        v-if="items.length === 0"
        class="no-connections-box"
      >
        <div
          v-if="lowerLevel"
          class="box-label"
        >
          {{ $t('cockpit.noConnectedElements') }}
        </div>
        <div
          v-else
          class="box-label"
        >
          {{ $t('cockpit.noConnectedParent') }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.inheritance-tree {
  margin-bottom: 46px;

  .label {
    font-size: 15px;
    font-weight: 500;
    color: black;
    display: flex;
    width: 100%;
    align-items: center;
    margin-left: 30px;
    margin-bottom: 4px;
  }

  .children {
    display: flex;
    flex-direction: column;
    gap: 5px;

    .no-connections-box {
      padding: 10px 30px 40px 30px;

      .box-label {
        font-size: 14px;
        color: #979797;
      }
    }
  }

  .connect-button, .reverse-select-button {
      padding: 2px 10px;
      font-size: 11px;
      font-weight: 500;

      i {
        margin-right: 4px;
      }
    }
}
</style>
